import React, {JSX, useEffect, useState} from 'react';
import styled, {IStyledComponent} from 'styled-components/native';
import Label from '../label';
import MultiSelectItem from './multiSelectItem';
import {MultiSelectPickerProps} from '../../interfaces/components/multiSelectPicker';

const Container = styled.View`
  width: 100%;
  z-index: 9999;
`;
const ContentContainer: IStyledComponent<'native', any> = styled.View`
  width: 100%;
  flex-direction: ${({layout}) => layout};
  ${({layout}) =>
    layout === 'row' &&
    `
  flex-wrap: wrap;
  justify-content: space-between;
  `};
`;

/**
 * The MultiSelectPicker component allows users to select multiple options from a list. It manages the selection state internally and updates the parent component via a callback with the current selection. This component is highly customizable, supporting custom styles, layout orientations, and an optional label for the picker group.
 *
 * @component
 * @param {Object} props - Properties passed to the MultiSelectPicker component.
 * @param {ViewStyle} props.style - Custom styles for the picker's container.
 * @param {string} props.accessibilityLabel - Accessibility label for the picker, enhancing its accessibility for users with assistive technologies.
 * @param {TextStyle} props.labelStyle - Custom styles for the label displayed above the picker, if provided.
 * @param {'row' | 'column'} props.layout - The layout orientation of the picker items, supporting either a row or column arrangement.
 * @param {Function} props.onSelectedChange - Callback function triggered when the selection changes, receiving an array of selected item ids.
 * @param {Array<MultiSelectItemProps>} props.multiSelectButtons - An array of properties for each selectable item within the picker. Each item must conform to the MultiSelectItemProps interface.
 * @param {string} props.label - An optional label for the picker, providing context or instructions for the selection.
 * @param {TextStyle} props.itemLabelStyle - Custom styles that can be applied to the labels of individual items within the picker. Overrides `labelStyle` for specific items if both are provided.
 * @returns {JSX.Element} The rendered MultiSelectPicker component with selectable items.
 */
const MultiSelectPicker: React.FC<MultiSelectPickerProps> = ({
  style,
  accessibilityLabel,
  labelStyle,
  layout = 'column',
  onSelectedChange,
  isOneSelection,
  multiSelectButtons,
  label,
  itemLabelStyle,
  isOverlay,
  isTipHidden,
  selectedIds,
  defaultSelectedIds = [],
  itemContainerStyle,
  withItemBorder,
  isTouchableOpacity,
  containerStyle,
  checkStyle,
  isRequired,
}: MultiSelectPickerProps): JSX.Element => {
  const [selected, setSelected] = useState<string[]>(defaultSelectedIds);
  const handlePress = (id: string) => {
    setSelected(prev => {
      let tempSelected = [id];
      if (prev.indexOf(id) !== -1) {
        tempSelected = prev.filter(item => item !== id);
      } else if (!isOneSelection) {
        tempSelected = [...prev, id];
      }
      onSelectedChange && onSelectedChange(tempSelected);
      return tempSelected;
    });
  };

  useEffect(() => {
    const tempSelected: string[] = (selectedIds || []).filter(
      (id): id is string => Boolean(id),
    );
    setSelected(tempSelected);
  }, [selectedIds]);
  return (
    <Container
      accessibilityLabel={accessibilityLabel}
      accessibilityRole={'checkbox'}
      style={style}>
      {label && (
        <Label
          isBold
          labelStyle={labelStyle}
          label={label}
          isRequired={isRequired}
        />
      )}
      <ContentContainer style={containerStyle} layout={layout}>
        {multiSelectButtons.map(button => (
          <MultiSelectItem
            {...button}
            checkStyle={checkStyle}
            key={button.id}
            isOverlay={isOverlay}
            labelStyle={button.labelStyle || itemLabelStyle}
            selected={selected.indexOf(button.id) !== -1}
            onPress={handlePress}
            isTipHidden={isTipHidden}
            containerStyle={[button?.containerStyle, itemContainerStyle]}
            withBorder={withItemBorder}
            isTouchableOpacity={isTouchableOpacity}
          />
        ))}
      </ContentContainer>
    </Container>
  );
};

export default MultiSelectPicker;
